import React from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Plus, Trash2 } from "lucide-react";

// Reusable list-of-strings editor (tag/line style).
export function StringListEditor({ value, onChange, placeholder, testid, rows = 1 }) {
  const arr = Array.isArray(value) ? value : [];
  const update = (i, v) => { const next = arr.slice(); next[i] = v; onChange(next); };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, ""]);
  const Field = rows > 1 ? Textarea : Input;
  return (
    <div className="space-y-2" data-testid={testid}>
      {arr.map((item, i) => (
        <div key={i} className="flex gap-2 items-start">
          <Field
            value={item || ""}
            onChange={(e) => update(i, e.target.value)}
            {...(rows > 1 ? { rows } : {})}
            className="text-sm flex-1"
            placeholder={placeholder}
            data-testid={`${testid}-item-${i}`}
          />
          <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} className="text-rose-600 hover:bg-rose-50" title="Remove" data-testid={`${testid}-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} className="text-[#111827] border-[#0B5FFF]/30" data-testid={`${testid}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add item
      </Button>
    </div>
  );
}

// Reusable list-of-objects editor where each row has the same set of text columns.
// `columns` = [{key, label, span?, rows?}]
export function ObjectListEditor({ value, onChange, columns, testid, addLabel = "Add row", blank }) {
  const arr = Array.isArray(value) ? value : [];
  const setRow = (i, k, v) => { const next = arr.slice(); next[i] = { ...next[i], [k]: v }; onChange(next); };
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, blank ? { ...blank } : columns.reduce((a, c) => ({ ...a, [c.key]: "" }), {})]);
  return (
    <div className="space-y-3" data-testid={testid}>
      {arr.map((row, i) => (
        <div key={i} className="p-3 border border-slate-200 rounded-md bg-white" data-testid={`${testid}-row-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 grid grid-cols-12 gap-2">
              {columns.map((c) => {
                const Field = c.rows ? Textarea : Input;
                return (
                  <div key={c.key} className={`col-span-12 ${c.span || "md:col-span-6"}`}>
                    <label className="text-[10px] uppercase tracking-wide text-slate-400">{c.label}</label>
                    <Field
                      value={row[c.key] || ""}
                      onChange={(e) => setRow(i, c.key, e.target.value)}
                      {...(c.rows ? { rows: c.rows } : {})}
                      placeholder={c.label}
                      className="text-sm mt-0.5"
                      data-testid={`${testid}-${i}-${c.key}`}
                    />
                  </div>
                );
              })}
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)} className="text-rose-600 hover:bg-rose-50 mt-4" data-testid={`${testid}-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} className="text-[#111827] border-[#0B5FFF]/30" data-testid={`${testid}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> {addLabel}
      </Button>
    </div>
  );
}
